import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {Switch} from '../../buttons/Swtich';
import {YesNoModal} from '../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {NoteEditModal} from './NoteEditModal';
import {VISIBILITY} from './factConstants';

const SOURCE_MANUAL = 'MANUAL';
const AUTHOR_ROLE_AGENCY = 'AGENCY';

// Vista "Notas": las notas del cliente (ClientMemory) — manuales y resúmenes generados por la
// extracción. La agencia las crea, edita, comparte y borra. Reusa los endpoints ya deployados.
class MemoryNotesViewClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            notes: [],
            loading: true,
            busyId: null,
            modalOpen: false,
            modalNote: null,   // null = crear; objeto = editar
            savingNote: false,
            deleteTarget: null,
            deleting: false,
        };
        this.handleSave = this.handleSave.bind(this);
        this.handleToggleVisibility = this.handleToggleVisibility.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    }

    componentDidMount() {
        this.fetchAll();
    }

    fetchAll() {
        const {t} = this.props;
        this.setState({loading: true});
        ClientMemoryApi.listMemories(this.props.clientId)
            .then((data) => {
                // El listado devuelve {memories} directo, sin {status}.
                if (!data || !Array.isArray(data.memories)) {
                    this.setState({loading: false});
                    toast.error(t('client_memory.toast.load_error'));
                    return;
                }
                this.setState({notes: data.memories, loading: false});
            })
            .catch(() => {
                this.setState({loading: false});
                toast.error(t('client_memory.toast.load_error'));
            });
    }

    handleSave(note, payload) {
        const {t} = this.props;
        const isCreate = !note;
        this.setState({savingNote: true});
        const request = isCreate
            ? ClientMemoryApi.createMemory({agency_client_id: this.props.clientId, ...payload})
            : ClientMemoryApi.patchMemory(note.id, {agency_client_id: this.props.clientId, ...payload});

        request
            .then((data) => {
                this.setState({savingNote: false});
                if (data && data.status === 'OK') {
                    if (isCreate) {
                        this.setState((prev) => ({notes: [data.data, ...prev.notes], modalOpen: false}));
                        toast.success(t('client_memory.toast.note_created'));
                    } else {
                        this.setState((prev) => ({
                            notes: prev.notes.map((n) => (n.id === data.data.id ? data.data : n)),
                            modalOpen: false,
                        }));
                        toast.success(t('client_memory.toast.note_edited'));
                    }
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({savingNote: false});
                toast.error(t('client_memory.toast.error'));
            });
    }

    handleToggleVisibility(note, shared) {
        const {t} = this.props;
        this.setState({busyId: note.id});
        ClientMemoryApi.patchMemory(note.id, {
            agency_client_id: this.props.clientId,
            visibility: shared ? VISIBILITY.SHARED : VISIBILITY.AGENCY_ONLY,
        }).then((data) => {
            this.setState({busyId: null});
            if (data && data.status === 'OK') {
                this.setState((prev) => ({notes: prev.notes.map((n) => (n.id === data.data.id ? data.data : n))}));
                toast.success(t(shared ? 'client_memory.toast.note_shared' : 'client_memory.toast.note_unshared'));
            } else {
                toast.error(t('client_memory.toast.error'));
            }
        }).catch(() => {
            this.setState({busyId: null});
            toast.error(t('client_memory.toast.error'));
        });
    }

    handleDeleteConfirm() {
        const {t} = this.props;
        const note = this.state.deleteTarget;
        if (!note) return;
        this.setState({deleting: true});
        ClientMemoryApi.deleteMemory(note.id, this.props.clientId)
            .then((data) => {
                this.setState({deleting: false, deleteTarget: null});
                if (data && data.status === 'OK') {
                    this.setState((prev) => ({notes: prev.notes.filter((n) => n.id !== note.id)}));
                    toast.success(t('client_memory.toast.note_deleted'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                this.setState({deleting: false, deleteTarget: null});
                toast.error(t('client_memory.toast.error'));
            });
    }

    _dateLabel(note) {
        if (!note.created_on) return '';
        return new Date(note.created_on).toLocaleDateString(this.props.i18n.language);
    }

    renderNote(note) {
        const {t} = this.props;
        const isGenerated = note.source && note.source !== SOURCE_MANUAL;
        const isShared = note.visibility === VISIBILITY.SHARED;
        const isAgency = note.author_role === AUTHOR_ROLE_AGENCY;
        return (
            <div className="cmem-note" key={note.id}>
                <div className="cmem-fact-main">
                    <p className="cmem-statement">{note.text}</p>
                    <div className="cmem-badges">
                        {isGenerated && (
                            <span className="cmem-badge cmem-origin -ai">{t('client_memory.notes.ai_summary')}</span>
                        )}
                        {!isAgency && (
                            <span className="cmem-badge cmem-origin -human">{t('client_memory.notes.from_client')}</span>
                        )}
                        <span className={`cmem-badge cmem-visibility ${isShared ? '-shared' : '-internal'}`}>
                            {isShared ? t('client_memory.visibility.shared') : t('client_memory.visibility.internal')}
                        </span>
                        {note.business_unit_name && (
                            <span className="cmem-badge cmem-type -background">{note.business_unit_name}</span>
                        )}
                    </div>
                    <div className="cmem-note-meta">
                        {note.author_name && <span>{note.author_name}</span>}
                        <span>{this._dateLabel(note)}</span>
                    </div>
                </div>
                <div className="cmem-fact-actions">
                    <button type="button" className="btn -negative cmem-sm" disabled={this.state.busyId === note.id}
                            onClick={() => this.setState({modalOpen: true, modalNote: note})}>
                        {t('client_memory.action.edit')}
                    </button>
                    {isAgency && (
                        <label className="cmem-share-toggle">
                            <Switch name={`note-share-${note.id}`} checked={isShared}
                                    disabled={this.state.busyId === note.id}
                                    onChange={(e) => this.handleToggleVisibility(note, e.target.checked)}
                                    style={{margin: 0}}/>
                            <span>{t('client_memory.visibility.share')}</span>
                        </label>
                    )}
                    <button type="button" className="cmem-link-danger" disabled={this.state.busyId === note.id}
                            onClick={() => this.setState({deleteTarget: note})}>
                        {t('client_memory.action.delete')}
                    </button>
                </div>
            </div>
        );
    }

    renderEmpty() {
        const {t} = this.props;
        return (
            <div className="cmem-empty">
                <FaIcon iconName="note-sticky" className="cmem-empty-icon"/>
                <h3>{t('client_memory.notes.empty_title')}</h3>
                <p>{t('client_memory.notes.empty_subtitle')}</p>
                <div className="cmem-empty-actions">
                    <button type="button" className="btn -alt cmem-empty-cta"
                            onClick={() => this.setState({modalOpen: true, modalNote: null})}>
                        <FaIcon iconName="plus"/>
                        {t('client_memory.notes.add')}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }

        return (
            <div className="cmem-notes">
                {this.state.notes.length === 0 ? this.renderEmpty() : (
                    <>
                        <header className="cmem-section-head">
                            <h3>{t('client_memory.notes.title')}</h3>
                            <span className="cmem-count-pill">{this.state.notes.length}</span>
                            <button type="button" className="btn -negative cmem-sm cmem-add-btn"
                                    onClick={() => this.setState({modalOpen: true, modalNote: null})}>
                                <FaIcon iconName="plus"/> {t('client_memory.notes.add')}
                            </button>
                        </header>
                        {this.state.notes.map((note) => this.renderNote(note))}
                    </>
                )}

                <NoteEditModal
                    isOpen={this.state.modalOpen}
                    note={this.state.modalNote}
                    saving={this.state.savingNote}
                    toggle={() => this.setState({modalOpen: false})}
                    onSave={this.handleSave}
                />
                <YesNoModal
                    skipRenderButton
                    isOpen={!!this.state.deleteTarget}
                    title={t('client_memory.notes.delete_title')}
                    question={t('client_memory.notes.delete_question')}
                    isSaving={this.state.deleting}
                    onYesAnswer={this.handleDeleteConfirm}
                    onNoAnswer={() => this.setState({deleteTarget: null})}
                />
            </div>
        );
    }
}

export const MemoryNotesView = withTranslation()(MemoryNotesViewClass);
